import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, Lock, Tag } from "lucide-react";
import toast from "react-hot-toast";

import {
  createMarketUpdateTemplate, updateMarketUpdateTemplate, getMergeTags,
} from "../api/prospectingReportsApi";
import { sanitizeHtml } from "../../../utils/sanitizeHtml";

const TEAL = "#55c5d0";

/**
 * Build a market update template.
 *
 * A plain HTML editor with a merge-tag palette rather than the GrapesJS builder
 * the Communications hub uses. These are short, text-led market emails an agent
 * writes once and reuses across suburbs; the drag-and-drop builder is aimed at
 * campaign EDMs and would be the wrong tool with a much heavier bundle.
 */
const TemplateEditorModal = ({ template, onClose, onSaved }) => {
  const isNew = !template?.id;
  const readOnly = Boolean(template?.readOnly);

  const [form, setForm] = useState({
    name: template?.name || "",
    subject: template?.subject || "",
    office_label: template?.office_label || "",
    body_html: template?.body_html || "",
    cover_image_url: template?.cover_image_url || "",
  });
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    getMergeTags()
      .then(({ data }) => setTags(data?.tags || []))
      .catch(() => setTags([]));
  }, []);

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm((prev) => ({ ...prev, [key]: e.target.value })),
    disabled: readOnly,
  });

  // Insert at the caret rather than appending, so a tag lands where the agent
  // was typing.
  const insertTag = (tag) => {
    const textarea = bodyRef.current;
    if (!textarea) {
      setForm((prev) => ({ ...prev, body_html: prev.body_html + tag }));
      return;
    }
    const { selectionStart, selectionEnd, value } = textarea;
    const next = value.slice(0, selectionStart) + tag + value.slice(selectionEnd);
    setForm((prev) => ({ ...prev, body_html: next }));
    requestAnimationFrame(() => {
      textarea.focus();
      const caret = selectionStart + tag.length;
      textarea.setSelectionRange(caret, caret);
    });
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Give the template a name.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await createMarketUpdateTemplate(form);
        toast.success("Template created.");
      } else {
        await updateMarketUpdateTemplate(template.id, form);
        toast.success("Template saved.");
      }
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save the template.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            {readOnly && <Lock className="w-4 h-4 text-gray-400" />}
            {isNew ? "New template" : readOnly ? template.name : `Edit ${template.name}`}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-gray-50 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 grid md:grid-cols-3 gap-5">
          <div className="md:col-span-2 space-y-3">
            {readOnly && (
              <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-3">
                This is a system template, so it cannot be edited. Duplicate it to make your own
                version.
              </p>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Template name</label>
              <input type="text" {...field("name")} placeholder="Monthly Market Update"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:bg-gray-50" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Email subject</label>
              <input type="text" {...field("subject")} placeholder="{{suburb}} market update"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:bg-gray-50" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Office label</label>
              <input type="text" {...field("office_label")}
                placeholder="Collings Real Estate - BANYULE"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:bg-gray-50" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Email body (HTML)
              </label>
              <textarea
                ref={bodyRef}
                rows={12}
                {...field("body_html")}
                placeholder="<p>Hi there,</p>"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs font-mono disabled:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#55c5d0]/40"
              />
            </div>
          </div>

          <div className="md:col-span-1">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5" /> Merge tags
            </p>
            <p className="text-[11px] text-gray-500 mb-2">
              {readOnly ? "These are replaced when an update is created." : "Click to insert at the cursor."}
            </p>
            <div className="space-y-1 max-h-[420px] overflow-y-auto pr-1">
              {tags.map((tag) => (
                <button
                  key={tag.tag}
                  type="button"
                  disabled={readOnly}
                  onClick={() => insertTag(tag.tag)}
                  className="w-full text-left p-2 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-default"
                >
                  <code className="text-[11px] font-bold" style={{ color: TEAL }}>{tag.tag}</code>
                  <p className="text-[10px] text-gray-500 leading-snug">{tag.description}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Live preview of the raw template, tags and all. */}
        <div className="px-5 pb-5">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Preview</p>
          <div
            className="border border-gray-200 rounded-lg p-4 max-h-56 overflow-y-auto text-sm text-gray-700"
            // Templates are shared, so the HTML may be a colleague's: sanitised
            // before it renders in this session.
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(form.body_html) || "<p class='text-gray-400'>Nothing yet.</p>" }}
          />
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50">
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <button type="button" onClick={submit} disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
              style={{ backgroundColor: TEAL }}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {isNew ? "Create template" : "Save changes"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateEditorModal;
